import { useCallback } from 'react';
import { useAppStore } from '../store';
import { saveTask, updateTask as fbUpdateTask, deleteTask as fbDeleteTask, onTasks } from '../firebase';
import type { Task, Priority, SmartViewId } from '../types';
import { nextRecurrence } from '../recurrence';
import { useEffect } from 'react';

export function useTasks(uid?: string) {
  const { tasks, setTasks, activeView, projects, searchQuery } = useAppStore();

  // Subscribe to Firestore
  useEffect(() => {
    if (!uid) return;
    return onTasks(uid, setTasks);
  }, [uid]);

  const addTask = useCallback(async (partial: Omit<Task, 'id' | 'uid' | 'createdAt' | 'updatedAt' | 'order'>) => {
    if (!uid) return;
    const now = Date.now();
    const task: Task = {
      ...partial,
      id: crypto.randomUUID(),
      uid,
      order: tasks.length,
      createdAt: now,
      updatedAt: now,
    };
    await saveTask(uid, task);
  }, [uid, tasks.length]);

  const updateTask = useCallback(async (id: string, changes: Partial<Task>) => {
    if (!uid) return;
    await fbUpdateTask(uid, id, changes);
  }, [uid]);

  const deleteTask = useCallback(async (id: string) => {
    if (!uid) return;
    await fbDeleteTask(uid, id);
  }, [uid]);

  const toggleComplete = useCallback(async (id: string) => {
    const task = tasks.find(t => t.id === id);
    if (!task) return;
    const willComplete = !task.completed;
    await updateTask(id, {
      completed: willComplete,
      completedAt: willComplete ? Date.now() : undefined,
    });

    // Completing a recurring task spawns its next occurrence (history is kept).
    if (willComplete && uid && task.recurrence !== 'none') {
      const next = nextRecurrence(task.dueDate ?? Date.now(), task.recurrence);
      if (next !== undefined) {
        const now = Date.now();
        await saveTask(uid, {
          ...task,
          id: crypto.randomUUID(),
          completed: false,
          completedAt: undefined,
          completedPomodoros: 0,
          dueDate: next,
          reminder: undefined,
          subtasks: task.subtasks.map(s => ({ ...s, completed: false, completedPomodoros: 0 })),
          order: tasks.length,
          createdAt: now,
          updatedAt: now,
        });
      }
    }
  }, [tasks, updateTask, uid]);

  const reorderTasks = useCallback(async (ids: string[]) => {
    if (!uid) return;
    await Promise.all(ids.map((id, order) => fbUpdateTask(uid, id, { order })));
  }, [uid]);

  // ── Filtered view ─────────────────────────────────────────────────────────

  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const todayEnd   = new Date(); todayEnd.setHours(23, 59, 59, 999);
  const tomorrowStart = new Date(todayStart); tomorrowStart.setDate(tomorrowStart.getDate() + 1);
  const tomorrowEnd   = new Date(todayEnd);   tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);
  const weekEnd       = new Date(todayEnd);   weekEnd.setDate(weekEnd.getDate() + 7);

  const filteredTasks = tasks.filter(t => {
    if (searchQuery) {
      return t.name.toLowerCase().includes(searchQuery.toLowerCase())
        || (t.notes ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    }
    switch (activeView as SmartViewId) {
      // "Hoy" = inbox (tareas sin fecha) + tareas con fecha de hoy
      case 'today':
        return !t.completed && (
          t.dueDate === undefined ||
          (t.dueDate >= todayStart.getTime() && t.dueDate <= todayEnd.getTime())
        );
      case 'tomorrow':
        return !t.completed && t.dueDate !== undefined
          && t.dueDate >= tomorrowStart.getTime()
          && t.dueDate <= tomorrowEnd.getTime();
      // Esta semana = pasado mañana hasta 7 días desde hoy
      case 'thisWeek':
        return !t.completed && t.dueDate !== undefined
          && t.dueDate > tomorrowEnd.getTime()
          && t.dueDate <= weekEnd.getTime();
      // Planificado = cualquier tarea con fecha (pasada o futura)
      case 'planned':
        return !t.completed && t.dueDate !== undefined;
      case 'completed':
        return t.completed;
      case 'overdue':
        return !t.completed && t.dueDate !== undefined && t.dueDate < todayStart.getTime();
      case 'highPriority':
        return !t.completed && t.priority === 'high';
      case 'all':
        return !t.completed;
      default:
        // Vista de etiqueta (tag:<name>) o de proyecto
        if (typeof activeView === 'string' && activeView.startsWith('tag:'))
          return !t.completed && t.tags.includes(activeView.slice(4));
        return !t.completed && t.projectId === activeView;
    }
  });

  return { tasks, filteredTasks, addTask, updateTask, deleteTask, toggleComplete, reorderTasks };
}

export interface DueGroup { id: string; label: string; tasks: Task[] }

/** Groups pending tasks for the "Todas" view: Vencidas, Hoy, Mañana, Esta semana, Más adelante, Sin fecha. */
export function groupByDue(tasks: Task[], now: Date = new Date()): DueGroup[] {
  const day = (offset: number) => { const d = new Date(now); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + offset); return d.getTime(); };
  const today = day(0), tomorrow = day(1), afterTomorrow = day(2), week = day(8);
  const groups: DueGroup[] = [
    { id: 'overdue', label: 'Vencidas', tasks: [] },
    { id: 'today', label: 'Hoy', tasks: [] },
    { id: 'tomorrow', label: 'Mañana', tasks: [] },
    { id: 'week', label: 'Esta semana', tasks: [] },
    { id: 'later', label: 'Más adelante', tasks: [] },
    { id: 'none', label: 'Sin fecha', tasks: [] },
  ];
  const at = (id: string) => groups.find((g) => g.id === id)!.tasks;
  for (const t of tasks) {
    const d = t.dueDate;
    if (d === undefined) at('none').push(t);
    else if (d < today) at('overdue').push(t);
    else if (d < tomorrow) at('today').push(t);
    else if (d < afterTomorrow) at('tomorrow').push(t);
    else if (d < week) at('week').push(t);
    else at('later').push(t);
  }
  for (const g of groups) if (g.id !== 'none') g.tasks.sort((a, b) => (a.dueDate ?? 0) - (b.dueDate ?? 0));
  return groups.filter((g) => g.tasks.length > 0);
}
